import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Card,
  Typography,
  Checkbox,
  Popconfirm,
  Divider,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useStep } from '../../../hooks/useStep';
import LoadingSpinner from '../util/LoadingSpinner';
import CommentField from '../../CommentField/CommentField';
import { parsedContent } from '../../../shared/interfaces/screeningContent.interface';
import { FormListFieldData } from 'antd/es/form/FormList';
import { useTranslation } from 'react-i18next';

const { Paragraph } = Typography;

interface ReferentFormProps<T> {
  title: parsedContent;
  description: parsedContent;
  permission: parsedContent;
  comment: string;
  commentApiUrl: string;
  handleSubmit: (values: T) => Promise<void>;
  getReferents: () => Promise<T | undefined>;
  children: (field: FormListFieldData) => React.ReactNode;
}

function ReferentForm<T>(props: ReferentFormProps<T>) {
  const [form] = Form.useForm();
  const { goToNextStep } = useStep();

  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation(['screening/referent', 'screening/general']);

  useEffect(() => {
    props
      .getReferents()
      .then((referents) => {
        if (!referents) return;
        form.setFieldsValue(referents);
      })
      .finally(() => setLoadingData(false));
  }, [form, props]);

  const proceedWithoutReferents = () => {
    form.setFieldsValue({ referents: [] });
    goToNextStep();
  };

  const handleSubmit = (values: T) => {
    setLoading(false);
    props.handleSubmit(values).finally(() => setLoading(false));
  };

  return loadingData ? (
    <LoadingSpinner />
  ) : (
    <>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={{
          referents: [{}],
        }}
      >
        <Card title={props.title} style={{ maxWidth: 500 }}>
          <Paragraph>{props.description}</Paragraph>
          <Form.List name="referents">
            {(fields, { add, remove }) => (
              <div
                style={{
                  display: 'flex',
                  rowGap: 16,
                  flexDirection: 'column',
                }}
              >
                {fields.map((field) => (
                  <Card
                    size="small"
                    title={`${t('L-Referent')} ${field.name + 1}`}
                    key={field.key}
                    extra={
                      fields.length > 1 ? (
                        <CloseOutlined
                          onClick={() => {
                            remove(field.name);
                          }}
                        />
                      ) : null
                    }
                  >
                    {props.children(field)}
                  </Card>
                ))}

                <Button type="dashed" onClick={() => add()} block>
                  {t('B-AddReferent')}
                </Button>
              </div>
            )}
          </Form.List>
          <Form.Item
            name="allowContact"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('EM-Permission') ?? '')),
              },
            ]}
          >
            <Checkbox>{props.permission}</Checkbox>
          </Form.Item>
          <Divider />
          <CommentField ApiUrl={props.commentApiUrl} comment={props.comment} />
        </Card>
        <Button
          style={{ float: 'right', marginTop: 20, marginBottom: 30 }}
          type="primary"
          htmlType="submit"
          loading={loading}
        >
          {t('B-Next', { ns: 'screening/general' })}
        </Button>
        <Popconfirm
          title={t('WT-ContinueWithoutReferents')}
          description={t('WD-ContinueWithoutReferents')}
          overlayStyle={{ width: 300 }}
          okText={t('O-Yes', { ns: 'screening/general' })}
          cancelText={t('O-No', { ns: 'screening/general' })}
          onConfirm={proceedWithoutReferents}
        >
          <Button
            style={{
              float: 'left',
              marginTop: 20,
              marginBottom: 30,
              marginRight: 10,
            }}
            type="link"
          >
            {t('B-ContinueWithoutReferents')}
          </Button>
        </Popconfirm>
      </Form>
    </>
  );
}

export default ReferentForm;
